import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Fade,
  FormControlLabel,
  Grid,
  Grow,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Casino as WheelIcon,
  Category as CategoryIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Header from '../Header';
import SquareImageCropper from './SquareImageCropper';
import PrizeLiveAward from './PrizeLiveAward';
import PrizeHistory from './PrizeHistory';
import {
  API_BASE,
  CATEGORY_COLORS,
  authHeaders as sharedAuthHeaders,
  mediaUrl,
  prizeMatchesCap,
  prizeIsCollage,
  prizeIsWheel,
  prizeStockLabel,
  prizeValueLabel,
  suggestTierId,
} from './prizePaletteShared';

const emptyCategory = { name: '', description: '', color: '#FFD54F', enabled: true };
const emptyPrize = {
  name: '',
  prize_type: 'prize',
  value_amount: '',
  value_text: '',
  tier_id: '',
  quantity: '',
  internal_note: '',
  image_url: '',
  image_original_url: '',
  image_crop: '',
  enabled: true,
  wheel_id: '',
};
const emptyTier = { label: '', cap_amount: '', enabled: true };

const parseCrop = (raw) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed) return null;
    if (Number(parsed.width) > 0 && Number(parsed.height) > 0) return parsed;
    // Legacy square crop → approximate 3:1 from the old size
    if (Number(parsed.size) > 0) {
      return {
        x: Number(parsed.x) || 0,
        y: Number(parsed.y) || 0,
        width: Number(parsed.size),
        height: Number(parsed.size) / 3,
      };
    }
  } catch {
    /* ignore */
  }
  return null;
};

const LIBRARY_FILTERS_KEY = 'prize_library_filters';

const readLibraryFilters = () => {
  try {
    const raw = sessionStorage.getItem(LIBRARY_FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const PrizePalette = ({ showHeader = true, initialTab = 0, embedded = false }) => {
  const savedFilters = useRef(readLibraryFilters());
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(savedFilters.current.categoryFilter || 'all');
  const [tierCap, setTierCap] = useState(
    savedFilters.current.tierCap == null || savedFilters.current.tierCap === ''
      ? null
      : Number(savedFilters.current.tierCap)
  );
  const [search, setSearch] = useState(savedFilters.current.search || '');

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [savingCategory, setSavingCategory] = useState(false);

  const [prizeDialogOpen, setPrizeDialogOpen] = useState(false);
  const [prizeCategory, setPrizeCategory] = useState(null);
  const [editingPrize, setEditingPrize] = useState(null);
  const [prizeForm, setPrizeForm] = useState(emptyPrize);
  const [savingPrize, setSavingPrize] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [savedCrop, setSavedCrop] = useState(null);
  const [posterCrop, setPosterCrop] = useState(null);

  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState(emptyTier);
  const [savingTier, setSavingTier] = useState(false);

  const [wheelsList, setWheelsList] = useState([]);
  const [wheelsLoading, setWheelsLoading] = useState(false);

  const authHeaders = useCallback((extra = {}) => sharedAuthHeaders(extra), []);

  const [toast, setToast] = useState('');

  const flash = (msg) => {
    setSuccess(msg);
    setToast(msg);
    setTimeout(() => setSuccess(null), 2500);
  };

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, tierRes] = await Promise.all([
        fetch(`${API_BASE}/api/prize-palette/categories?withPrizes=true`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/prize-palette/tiers`, { headers: authHeaders() }),
      ]);
      const data = await res.json();
      const tierData = await tierRes.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to load prize palette');
        return;
      }
      setCategories(data.categories || []);
      setTiers(tierData.tiers || []);
    } catch (err) {
      console.error('fetchCatalog', err);
      setError('Failed to load prize palette');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(LIBRARY_FILTERS_KEY, JSON.stringify({
        categoryFilter,
        tierCap,
        search,
      }));
    } catch {
      /* private mode or full storage */
    }
  }, [categoryFilter, tierCap, search]);

  useEffect(() => {
    if (loading) return;
    if (
      categoryFilter !== 'all'
      && !categories.some((cat) => String(cat.id) === String(categoryFilter))
    ) {
      setCategoryFilter('all');
    }
    if (
      tierCap != null
      && !tiers.some((tier) => Number(tier.cap_amount) === Number(tierCap))
    ) {
      setTierCap(null);
    }
  }, [loading, categories, tiers, categoryFilter, tierCap]);

  const openCategoryDialog = (category = null) => {
    setEditingCategory(category);
    setCategoryForm(
      category
        ? {
            name: category.name || '',
            description: category.description || '',
            color: category.color || '#FFD54F',
            enabled: !!category.enabled,
          }
        : emptyCategory
    );
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      setError('Category name is required');
      return;
    }
    try {
      setSavingCategory(true);
      setError(null);
      const url = editingCategory
        ? `${API_BASE}/api/prize-palette/categories/${editingCategory.id}`
        : `${API_BASE}/api/prize-palette/categories`;
      const res = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(categoryForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to save category');
        return;
      }
      setCategoryDialogOpen(false);
      flash(editingCategory ? 'Category updated' : 'Category created');
      await fetchCatalog();
    } catch (err) {
      console.error('saveCategory', err);
      setError('Failed to save category');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}" and all its prizes?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/categories/${category.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to delete category');
        return;
      }
      flash('Category deleted');
      fetchCatalog();
    } catch (err) {
      console.error('deleteCategory', err);
      setError('Failed to delete category');
    }
  };

  const loadWheels = async () => {
    if (wheelsList.length > 0) return;
    try {
      setWheelsLoading(true);
      const res = await fetch(`${API_BASE}/api/prize-palette/wheels`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setWheelsList(data.wheels || []);
    } catch (err) {
      console.error('loadWheels', err);
    } finally {
      setWheelsLoading(false);
    }
  };

  const openPrizeDialog = (category, prize = null, type = 'prize') => {
    setPrizeCategory(category);
    setEditingPrize(prize);
    const prizeType = prize
      ? (prizeIsCollage(prize) ? 'collage' : prizeIsWheel(prize) ? 'wheel' : 'prize')
      : (type === 'collage' ? 'collage' : type === 'wheel' ? 'wheel' : 'prize');
    const form = prize
      ? {
          name: prize.name || '',
          prize_type: prizeType,
          value_amount: prize.value_amount ?? '',
          value_text: prize.value_text || '',
          tier_id: prize.tier_id || suggestTierId(tiers, prize.value_amount),
          quantity: prize.quantity == null ? '' : prize.quantity,
          internal_note: prize.internal_note || '',
          image_url: prize.image_url || '',
          image_original_url: prize.image_original_url || prize.image_url || '',
          image_crop: prize.image_crop || '',
          enabled: !!prize.enabled,
          wheel_id: prize.wheel_id ? String(prize.wheel_id) : '',
        }
      : { ...emptyPrize, prize_type: prizeType };
    setPrizeForm(form);
    if (prizeType === 'wheel') loadWheels();
    const original = form.image_original_url || form.image_url;
    setImagePreview(original ? mediaUrl(original) : '');
    setSavedCrop(parseCrop(form.image_crop));
    setPosterCrop(parseCrop(form.image_crop));
    setPrizeDialogOpen(true);
  };

  const setPrizeType = (prize_type) => {
    setPrizeForm((prev) => ({
      ...prev,
      prize_type,
      image_crop: '',
      ...(prize_type === 'collage' || prize_type === 'wheel'
        ? { value_amount: '', value_text: '', tier_id: '', quantity: '' }
        : {}),
      ...(prize_type !== 'wheel' ? { wheel_id: '' } : {}),
    }));
    setSavedCrop(null);
    setPosterCrop(null);
    if (prize_type === 'wheel') loadWheels();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API_BASE}/api/prize-palette/upload-image`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to upload image');
        return;
      }
      setPrizeForm((prev) => ({
        ...prev,
        image_original_url: data.imageUrl,
        image_url: data.imageUrl,
        image_crop: '',
      }));
      setImagePreview(mediaUrl(data.imageUrl));
      setSavedCrop(null);
      setPosterCrop(null);
    } catch (err) {
      console.error('upload image', err);
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const savePrize = async () => {
    if (!prizeCategory) return;
    const isCollage = prizeForm.prize_type === 'collage';
    const isWheel = prizeForm.prize_type === 'wheel';
    const noTierType = isCollage || isWheel;
    if (!prizeForm.name.trim()) {
      setError(isCollage ? 'Collage name is required' : isWheel ? 'Wheel name is required' : 'Prize name is required');
      return;
    }
    if (isWheel && !prizeForm.wheel_id) {
      setError('Select a wheel from the list');
      return;
    }
    if (!noTierType) {
      if (prizeForm.value_amount === '' || Number.isNaN(Number(prizeForm.value_amount)) || Number(prizeForm.value_amount) < 0) {
        setError('Enter a prize value of 0 or more');
        return;
      }
    }
    const quantityText = String(prizeForm.quantity ?? '').trim();
    let quantity = null;
    if (!noTierType && quantityText !== '') {
      const amount = Number(quantityText);
      if (!Number.isInteger(amount) || amount < 1) {
        setError('Quantity must be a whole number, or leave it empty for unlimited');
        return;
      }
      quantity = amount;
    }

    let imageUrl = prizeForm.image_url;
    let imageOriginalUrl = prizeForm.image_original_url || prizeForm.image_url || '';
    let imageCrop = prizeForm.image_crop || '';

    if (imageOriginalUrl) {
      if (!posterCrop) {
        setError('Wait for the image preview to finish loading, then adjust the crop');
        return;
      }
      try {
        const cropRes = await fetch(`${API_BASE}/api/prize-palette/crop-image`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            sourceUrl: imageOriginalUrl,
            x: posterCrop.x,
            y: posterCrop.y,
            width: posterCrop.width,
            height: posterCrop.height,
            aspect: isCollage || isWheel ? 'square' : 'banner',
            prize_type: isCollage ? 'collage' : isWheel ? 'wheel' : 'prize',
          }),
        });
        const cropData = await cropRes.json();
        if (!cropData.success) {
          setError(cropData.error || 'Could not apply the image crop');
          return;
        }
        imageUrl = cropData.imageUrl;
        imageCrop = JSON.stringify(cropData.crop);
      } catch {
        setError('Could not apply the image crop');
        return;
      }
    }

    try {
      setSavingPrize(true);
      setError(null);
      const payload = {
        name: prizeForm.name.trim(),
        prize_type: isCollage ? 'collage' : isWheel ? 'wheel' : 'prize',
        value_amount: noTierType ? 0 : Number(prizeForm.value_amount),
        value_text: noTierType ? '' : prizeForm.value_text || '',
        tier_id: noTierType ? null : (prizeForm.tier_id || suggestTierId(tiers, prizeForm.value_amount)),
        quantity: noTierType ? null : quantity,
        internal_note: prizeForm.internal_note || '',
        image_url: imageUrl || '',
        image_original_url: imageOriginalUrl || '',
        image_crop: imageCrop || '',
        enabled: prizeForm.enabled,
        ...(isWheel ? { wheel_id: Number(prizeForm.wheel_id) || null } : {}),
      };
      const url = editingPrize
        ? `${API_BASE}/api/prize-palette/prizes/${editingPrize.id}`
        : `${API_BASE}/api/prize-palette/categories/${prizeCategory.id}/prizes`;
      const res = await fetch(url, {
        method: editingPrize ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to save prize');
        return;
      }
      setPrizeDialogOpen(false);
      flash(editingPrize
        ? (isCollage ? 'Collage updated' : isWheel ? 'Wheel updated' : 'Prize updated')
        : (isCollage ? 'Collage created' : isWheel ? 'Wheel created' : 'Prize created'));
      fetchCatalog();
    } catch (err) {
      console.error('savePrize', err);
      setError('Failed to save prize');
    } finally {
      setSavingPrize(false);
    }
  };

  const deletePrize = async (prize) => {
    if (!window.confirm(`Delete prize "${prize.name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/prizes/${prize.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to delete prize');
        return;
      }
      flash('Prize deleted');
      fetchCatalog();
    } catch (err) {
      console.error('deletePrize', err);
      setError('Failed to delete prize');
    }
  };

  const togglePrizeEnabled = async (prize) => {
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/prizes/${prize.id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ enabled: !prize.enabled }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to update prize');
        return;
      }
      fetchCatalog();
    } catch (err) {
      console.error('toggle prize', err);
    }
  };

  const saveTier = async () => {
    if (!tierForm.label.trim()) {
      setError('Upto label is required');
      return;
    }
    if (!Number(tierForm.cap_amount) || Number(tierForm.cap_amount) <= 0) {
      setError('Upto amount must be greater than 0');
      return;
    }
    try {
      setSavingTier(true);
      setError(null);
      const url = editingTier
        ? `${API_BASE}/api/prize-palette/tiers/${editingTier.id}`
        : `${API_BASE}/api/prize-palette/tiers`;
      const res = await fetch(url, {
        method: editingTier ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          label: tierForm.label.trim(),
          cap_amount: Number(tierForm.cap_amount),
          enabled: tierForm.enabled,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to save Upto tier');
        return;
      }
      setEditingTier(null);
      setTierForm(emptyTier);
      flash(editingTier ? 'Upto tier updated' : 'Upto tier created');
      await fetchCatalog();
    } catch (err) {
      console.error('saveTier', err);
      setError('Failed to save Upto tier');
    } finally {
      setSavingTier(false);
    }
  };

  const deleteTier = async (tier) => {
    if (!window.confirm(`Delete ${tier.label}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/tiers/${tier.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to delete Upto tier');
        return;
      }
      flash('Upto tier deleted');
      fetchCatalog();
    } catch (err) {
      console.error('deleteTier', err);
      setError('Failed to delete Upto tier');
    }
  };

  useEffect(() => {
    if (initialTab != null) setTab(initialTab);
  }, [initialTab]);

  return (
    <Box sx={{ minHeight: embedded ? '100%' : '100vh', bgcolor: '#0e0e0c' }}>
      {showHeader ? <Header /> : null}
      <Container maxWidth="lg" sx={{ py: embedded ? 2 : 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3.5, flexWrap: 'wrap' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(255,213,79,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TrophyIcon sx={{ fontSize: 28, color: '#FFD54F' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: 24, sm: 30 }, letterSpacing: -0.5 }}>
              Prize Palette
            </Typography>
            <Typography variant="body2" sx={{ color: '#777', fontSize: 13, mt: 0.25 }}>
              Build categories and Upto shelves, then show or award a prize to one player
            </Typography>
          </Box>
          {tab === 1 && (
            <Fade in>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined"
                  onClick={() => { setEditingTier(null); setTierForm(emptyTier); setTierDialogOpen(true); }}
                  sx={{ borderColor: '#333', color: '#aaa', borderRadius: 2, textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: '#555', bgcolor: '#1a1a1a' } }}>
                  Upto tiers
                </Button>
                <Button size="small" variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  onClick={fetchCatalog} disabled={loading}
                  sx={{ borderColor: '#333', color: '#aaa', borderRadius: 2, textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: '#555', bgcolor: '#1a1a1a' } }}>
                  Refresh
                </Button>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => openCategoryDialog()}
                  sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2, boxShadow: '0 4px 16px rgba(255,213,79,0.2)', '&:hover': { bgcolor: '#FFC107' } }}>
                  Create Category
                </Button>
              </Box>
            </Fade>
          )}
        </Box>

        <Tabs value={tab} onChange={(_, value) => setTab(value)}
          sx={{
            mb: 3,
            '& .MuiTab-root': { color: '#666', fontWeight: 700, textTransform: 'none', fontSize: 14, minHeight: 42, transition: 'color 0.2s' },
            '& .Mui-selected': { color: '#FFD54F' },
            '& .MuiTabs-indicator': { bgcolor: '#FFD54F', height: 3, borderRadius: 2 },
          }}>
          <Tab label="Live Award" />
          <Tab label="Library" />
          <Tab label="History" />
        </Tabs>

        {tab === 0 && <PrizeLiveAward />}
        {tab === 2 && <PrizeHistory />}
        {tab === 1 && (
        <Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip label="All categories" size="small" onClick={() => setCategoryFilter('all')}
            sx={{ fontWeight: 700, fontSize: 12, bgcolor: categoryFilter === 'all' ? '#FFD54F' : '#1e1e1e', color: categoryFilter === 'all' ? '#111' : '#aaa', border: categoryFilter === 'all' ? 'none' : '1px solid #333' }} />
          {categories.map((category) => (
            <Chip key={category.id} label={category.name} size="small"
              onClick={() => setCategoryFilter(String(category.id))}
              sx={{
                fontWeight: 700, fontSize: 12,
                bgcolor: String(categoryFilter) === String(category.id) ? (category.color || '#FFD54F') : '#1e1e1e',
                color: String(categoryFilter) === String(category.id) ? '#111' : '#aaa',
                border: String(categoryFilter) === String(category.id) ? 'none' : '1px solid #333',
              }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5, alignItems: 'center' }}>
          <Chip label="All values" size="small" onClick={() => setTierCap(null)}
            sx={{ fontWeight: 700, fontSize: 12, bgcolor: tierCap == null ? '#FFD54F' : '#1e1e1e', color: tierCap == null ? '#111' : '#aaa', border: tierCap == null ? 'none' : '1px solid #333' }} />
          {tiers.filter((tier) => tier.enabled).map((tier) => (
            <Chip key={tier.id} label={tier.label} size="small"
              onClick={() => setTierCap(Number(tier.cap_amount))}
              sx={{
                fontWeight: 700, fontSize: 12,
                bgcolor: Number(tierCap) === Number(tier.cap_amount) ? '#FFD54F' : '#1e1e1e',
                color: Number(tierCap) === Number(tier.cap_amount) ? '#111' : '#aaa',
                border: Number(tierCap) === Number(tier.cap_amount) ? 'none' : '1px solid #333',
              }} />
          ))}
          <TextField size="small" placeholder="Search prizes…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200, ml: { md: 'auto' },
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#1a1a1a', '& fieldset': { borderColor: '#2a2a2a' }, '&:hover fieldset': { borderColor: '#444' } },
            }}
            InputProps={{ sx: { color: '#fff', fontSize: 13, height: 34 } }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FFD54F' }} />
          </Box>
        ) : categories.length === 0 ? (
          <Paper sx={{ p: 8, textAlign: 'center', bgcolor: '#141414', border: '1px solid #2a2a2a', borderRadius: 3 }}>
            <CategoryIcon sx={{ fontSize: 52, color: '#333', mb: 1.5 }} />
            <Typography sx={{ color: '#666', mb: 2.5, fontWeight: 600 }}>No category sets yet</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCategoryDialog()}
              sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2, boxShadow: '0 4px 16px rgba(255,213,79,0.2)' }}>
              Create first category
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {categories.map((category) => {
              if (categoryFilter !== 'all' && String(category.id) !== String(categoryFilter)) return null;
              const query = search.trim().toLowerCase();
              const prizes = (category.prizes || []).filter((prize) => {
                if (query && !String(prize.name || '').toLowerCase().includes(query)) return false;
                return prizeMatchesCap(prize, tierCap);
              });
              if ((query || tierCap != null) && prizes.length === 0) return null;
              return (
                <Paper
                  key={category.id}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    bgcolor: '#141414',
                    border: '1px solid #2a2a2a',
                    borderLeft: `4px solid ${category.color || '#FFD54F'}`,
                    borderRadius: 3,
                    opacity: category.enabled ? 1 : 0.6,
                    transition: 'border-color 0.3s ease, opacity 0.3s',
                    '&:hover': { borderColor: '#3a3a3a' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <CategoryIcon sx={{ color: '#FFD54F' }} />
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                          {category.name}
                        </Typography>
                        {!category.enabled && (
                          <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                            Disabled
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#777' }}>
                          {prizes.length} prize{prizes.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      {category.description ? (
                        <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                          {category.description}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => openPrizeDialog(category, null, 'prize')}
                        sx={{ color: '#FFD54F', textTransform: 'none' }}
                      >
                        Add Prize
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => openPrizeDialog(category, null, 'collage')}
                        sx={{ color: '#90CAF9', textTransform: 'none' }}
                      >
                        Add Collage
                      </Button>
                      <Button
                        size="small"
                        startIcon={<WheelIcon />}
                        onClick={() => openPrizeDialog(category, null, 'wheel')}
                        sx={{ color: '#CE93D8', textTransform: 'none' }}
                      >
                        Add Wheel
                      </Button>
                      <Tooltip title="Edit category">
                        <IconButton size="small" onClick={() => openCategoryDialog(category)} sx={{ color: '#bbb' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete category">
                        <IconButton size="small" onClick={() => deleteCategory(category)} sx={{ color: '#f44336' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {prizes.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed #333', borderRadius: 2.5, bgcolor: '#111' }}>
                      <TrophyIcon sx={{ fontSize: 36, color: '#333', mb: 1 }} />
                      <Typography sx={{ color: '#666', mb: 1.5, fontWeight: 600, fontSize: 14 }}>No prizes in this set yet</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => openPrizeDialog(category)}
                        sx={{ color: '#FFD54F', borderColor: '#FFD54F' }}
                      >
                        Add prize
                      </Button>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {prizes.map((prize) => {
                        const collage = prizeIsCollage(prize);
                        const wheel = prizeIsWheel(prize);
                        return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={prize.id}>
                          <Card
                            sx={{
                              bgcolor: '#161616',
                              border: '1px solid transparent',
                              borderRadius: 2.5,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              opacity: prize.enabled ? 1 : 0.45,
                              cursor: 'pointer',
                              transition: 'all 0.25s ease',
                              '&:hover': {
                                borderColor: wheel ? '#CE93D844' : collage ? '#90CAF944' : '#FFD54F44',
                                transform: 'translateY(-3px)',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                              },
                            }}
                            onClick={() => openPrizeDialog(category, prize)}
                          >
                            {prize.image_url ? (
                              <CardMedia
                                component="img"
                                image={mediaUrl(prize.image_url)}
                                alt={prize.name}
                                sx={{
                                  width: '100%',
                                  aspectRatio: collage || wheel ? '1 / 1' : '3 / 1',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                  display: 'block',
                                  bgcolor: '#111',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  aspectRatio: collage || wheel ? '1 / 1' : '3 / 1',
                                  bgcolor: '#2a2a2a',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <TrophyIcon sx={{ fontSize: 40, color: '#555' }} />
                              </Box>
                            )}
                            <CardContent sx={{ flex: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}>
                                  {prize.name}
                                </Typography>
                                {collage ? (
                                  <Chip label="Collage" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#1e3a5f', color: '#90CAF9' }} />
                                ) : wheel ? (
                                  <Chip label="Wheel" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#2e1e3f', color: '#CE93D8' }} />
                                ) : null}
                              </Box>
                              {!collage && prizeValueLabel(prize) ? (
                                <Typography variant="body2" sx={{ color: '#FFD54F', mt: 0.5 }}>
                                  {prizeValueLabel(prize)}
                                </Typography>
                              ) : null}
                              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                                {(collage || wheel) ? prizeStockLabel(prize) : `${prize.tier_label || 'No shelf'} · ${prizeStockLabel(prize)}`}
                              </Typography>
                            </CardContent>
                            <CardActions
                              sx={{ justifyContent: 'space-between', px: 1.5, pb: 1.5, pt: 0 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={!!prize.enabled}
                                    onChange={() => togglePrizeEnabled(prize)}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD54F' },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#FFD54F',
                                      },
                                    }}
                                  />
                                }
                                label={<Typography variant="caption" sx={{ color: '#999' }}>On</Typography>}
                              />
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => openPrizeDialog(category, prize)}
                                  sx={{ color: '#bbb' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => deletePrize(prize)}
                                  sx={{ color: '#f44336' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardActions>
                          </Card>
                        </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
        </Box>
        )}
      </Container>

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast}
        ContentProps={{ sx: { bgcolor: '#FFD54F', color: '#111', fontWeight: 700, borderRadius: 2, boxShadow: '0 8px 30px rgba(255,213,79,0.3)' } }} />

      {/* Category dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#161616', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category name"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={categoryForm.description}
            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={categoryForm.enabled}
                onChange={(e) => setCategoryForm({ ...categoryForm, enabled: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD54F' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD54F' },
                }}
              />
            }
            label="Enabled"
          />
          <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 2, mb: 1 }}>
            Popup color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {CATEGORY_COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => setCategoryForm({ ...categoryForm, color })}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: color,
                  cursor: 'pointer',
                  border: categoryForm.color === color ? '3px solid #fff' : '3px solid transparent',
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)} sx={{ color: '#aaa' }}>
            Cancel
          </Button>
          <Button onClick={saveCategory} disabled={savingCategory} variant="contained"
            sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#FFC107' } }}>
            {savingCategory ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prize dialog */}
      <Dialog open={prizeDialogOpen} onClose={() => setPrizeDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#161616', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingPrize
            ? (prizeForm.prize_type === 'collage' ? 'Edit Collage' : prizeForm.prize_type === 'wheel' ? 'Edit Wheel' : 'Edit Prize')
            : (prizeForm.prize_type === 'collage' ? 'Add Collage' : prizeForm.prize_type === 'wheel' ? 'Add Wheel' : 'Add Prize')}
          {prizeCategory ? (
            <Typography variant="body2" sx={{ color: '#999', fontWeight: 400, mt: 0.5 }}>
              in {prizeCategory.name}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 0.5 }}>
            <Chip
              label="Prize"
              onClick={() => setPrizeType('prize')}
              sx={{
                fontWeight: 800,
                bgcolor: prizeForm.prize_type === 'prize' ? '#FFD54F' : '#1e1e1e',
                color: prizeForm.prize_type === 'prize' ? '#111' : '#aaa',
                border: prizeForm.prize_type === 'prize' ? 'none' : '1px solid #333',
              }}
            />
            <Chip
              label="Collage"
              onClick={() => setPrizeType('collage')}
              sx={{
                fontWeight: 800,
                bgcolor: prizeForm.prize_type === 'collage' ? '#90CAF9' : '#1e1e1e',
                color: prizeForm.prize_type === 'collage' ? '#111' : '#aaa',
                border: prizeForm.prize_type === 'collage' ? 'none' : '1px solid #333',
              }}
            />
            <Chip
              label="Wheel"
              icon={<WheelIcon sx={{ fontSize: 16 }} />}
              onClick={() => setPrizeType('wheel')}
              sx={{
                fontWeight: 800,
                bgcolor: prizeForm.prize_type === 'wheel' ? '#CE93D8' : '#1e1e1e',
                color: prizeForm.prize_type === 'wheel' ? '#111' : '#aaa',
                border: prizeForm.prize_type === 'wheel' ? 'none' : '1px solid #333',
                '& .MuiChip-icon': { color: prizeForm.prize_type === 'wheel' ? '#111' : '#aaa' },
              }}
            />
          </Box>
          {prizeForm.prize_type === 'collage' ? (
            <Typography variant="body2" sx={{ color: '#777', mb: 2, fontSize: 13 }}>
              Square image for show / hide only — not awarded to a player.
            </Typography>
          ) : prizeForm.prize_type === 'wheel' ? (
            <Typography variant="body2" sx={{ color: '#777', mb: 2, fontSize: 13 }}>
              Select a wheel from your library. In Live Award, you control the wheel for the selected player.
            </Typography>
          ) : null}

          <Box sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
            {imagePreview ? (
              <SquareImageCropper
                key={`${imagePreview}-${prizeForm.prize_type}`}
                src={imagePreview}
                initialCrop={savedCrop}
                onChange={setPosterCrop}
                aspectMode={prizeForm.prize_type === 'collage' || prizeForm.prize_type === 'wheel' ? 'square' : 'banner'}
              />
            ) : (
              <Box
                sx={{
                  width: prizeForm.prize_type === 'collage' || prizeForm.prize_type === 'wheel' ? 280 : 360,
                  height: prizeForm.prize_type === 'collage' || prizeForm.prize_type === 'wheel' ? 280 : 120,
                  mx: 'auto',
                  bgcolor: '#2a2a2a',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <Typography sx={{ color: '#666', px: 2 }}>
                  {prizeForm.prize_type === 'collage' || prizeForm.prize_type === 'wheel'
                    ? 'Upload an image, then crop to 600 × 600 (1:1)'
                    : 'Upload an image, then drag and zoom to a 600 × 200 crop'}
                </Typography>
              </Box>
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadIcon />}
              disabled={uploading}
              sx={{
                color: prizeForm.prize_type === 'collage' ? '#90CAF9' : '#FFD54F',
                borderColor: prizeForm.prize_type === 'collage' ? '#90CAF9' : '#FFD54F',
              }}
            >
              {uploading ? 'Uploading…' : 'Upload Image'}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>

          <TextField
            fullWidth
            label={prizeForm.prize_type === 'collage' ? 'Collage name' : prizeForm.prize_type === 'wheel' ? 'Wheel prize name' : 'Prize name'}
            value={prizeForm.name}
            onChange={(e) => setPrizeForm({ ...prizeForm, name: e.target.value })}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          {prizeForm.prize_type === 'wheel' ? (
            <TextField
              select
              fullWidth
              label="Select wheel"
              value={prizeForm.wheel_id ? String(prizeForm.wheel_id) : ''}
              onChange={(e) => setPrizeForm({ ...prizeForm, wheel_id: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              SelectProps={{ native: true, sx: { color: '#fff' } }}
              disabled={wheelsLoading}
              helperText={wheelsLoading ? 'Loading wheels…' : ''}
              FormHelperTextProps={{ sx: { color: '#888' } }}
            >
              <option value="">Choose a wheel</option>
              {wheelsList.map((w) => (
                <option key={w.wheel_id} value={w.wheel_id}>
                  {w.wheel_name} ({w.slice_count} {w.slice_count === 1 ? 'slice' : 'slices'})
                  {!w.is_active ? ' (inactive)' : ''}
                </option>
              ))}
            </TextField>
          ) : null}
          {prizeForm.prize_type !== 'collage' && prizeForm.prize_type !== 'wheel' ? (
            <>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={prizeForm.value_amount}
                onChange={(e) => {
                  const value_amount = e.target.value;
                  setPrizeForm({
                    ...prizeForm,
                    value_amount,
                    tier_id: suggestTierId(tiers, value_amount),
                  });
                }}
                placeholder="12"
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
              <TextField
                fullWidth
                label="Value label (optional)"
                value={prizeForm.value_text}
                onChange={(e) => setPrizeForm({ ...prizeForm, value_text: e.target.value })}
                placeholder="e.g. Free drink"
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
              <TextField
                select
                fullWidth
                label="Upto shelf"
                value={prizeForm.tier_id ? String(prizeForm.tier_id) : ''}
                onChange={(e) => setPrizeForm({ ...prizeForm, tier_id: e.target.value })}
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                SelectProps={{ native: true, sx: { color: '#fff' } }}
              >
                <option value="">Choose from the value</option>
                {tiers
                  .filter((tier) => tier.enabled || String(tier.id) === String(prizeForm.tier_id))
                  .map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.label}
                  </option>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                placeholder="Unlimited"
                helperText="Leave empty for unlimited"
                value={prizeForm.quantity}
                onChange={(e) => setPrizeForm({ ...prizeForm, quantity: e.target.value })}
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' }, inputProps: { min: 1, step: 1 } }}
                FormHelperTextProps={{ sx: { color: '#888' } }}
              />
            </>
          ) : null}
          <TextField
            fullWidth
            label="Internal note (players never see this)"
            value={prizeForm.internal_note}
            onChange={(e) => setPrizeForm({ ...prizeForm, internal_note: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={prizeForm.enabled}
                onChange={(e) => setPrizeForm({ ...prizeForm, enabled: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD54F' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD54F' },
                }}
              />
            }
            label="Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrizeDialogOpen(false)} sx={{ color: '#666', borderRadius: 2 }}>Cancel</Button>
          <Button onClick={savePrize} disabled={savingPrize || uploading} variant="contained"
            sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#FFC107' } }}>
            {savingPrize ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#161616', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Upto tiers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
            Shelves have a name and a cap. Images stay on each prize.
          </Typography>
          {tiers.map((tier) => (
            <Box key={tier.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ flex: 1 }}>
                {tier.label}
                <Typography component="span" variant="caption" sx={{ color: '#777', ml: 1 }}>
                  cap {tier.cap_amount} · {tier.prize_count || 0} prizes
                </Typography>
              </Typography>
              <IconButton
                size="small"
                sx={{ color: '#bbb' }}
                onClick={() => {
                  setEditingTier(tier);
                  setTierForm({
                    label: tier.label,
                    cap_amount: tier.cap_amount,
                    enabled: !!tier.enabled,
                  });
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#f44336' }} onClick={() => deleteTier(tier)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <TextField
            fullWidth
            label="Label"
            value={tierForm.label}
            onChange={(e) => setTierForm({ ...tierForm, label: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          <TextField
            fullWidth
            type="number"
            label="Cap amount"
            value={tierForm.cap_amount}
            onChange={(e) => setTierForm({ ...tierForm, cap_amount: e.target.value })}
            sx={{ mb: 1 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)} sx={{ color: '#aaa' }}>
            Close
          </Button>
          <Button
            onClick={saveTier}
            disabled={savingTier}
            variant="contained"
            sx={{ bgcolor: '#FFD54F', color: '#111' }}
          >
            {savingTier ? 'Saving…' : editingTier ? 'Update tier' : 'Add tier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrizePalette;
